import { createContext, useContext, useEffect, useReducer } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, doc, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { auth, db } from "../firebase";
import AuthReducer from "./AuthReducer";

const INITIAL_STATE = {
  currentUser: JSON.parse(localStorage.getItem("user")) || null,
  claims: null,
  agency: null,
  boardId: null,
  mode: null, // 'machine' | 'legacy' | null
  isStationboard: false,
  boardConfig: null,
  authReady: false,
};

export const AuthContext = createContext(INITIAL_STATE);
export const useAuth = () => useContext(AuthContext);

export const AuthContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(AuthReducer, INITIAL_STATE);

  // Persist currentUser for a fast first paint on reload (kiosk boot).
  useEffect(() => {
    localStorage.setItem("user", JSON.stringify(state.currentUser));
  }, [state.currentUser]);

  // Resolve the signed-in account into one of two modes. Custom claims aren't in
  // the freshly-issued token, so force a refresh before reading them.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        dispatch({ type: "LOGOUT" });
        return;
      }
      try {
        await user.getIdToken(true);
        const { claims } = await user.getIdTokenResult();

        // (a) New per-board machine account — identified by custom claims.
        if (claims.stationboard === true && claims.agency) {
          dispatch({
            type: "SET_AUTH",
            payload: {
              currentUser: user,
              claims,
              agency: claims.agency,
              boardId: claims.stationboardId || user.uid,
              mode: "machine",
              isStationboard: true,
            },
          });
          return;
        }

        // (b) Legacy shared account — a /users doc flagged stationboard:true.
        const snap = await getDocs(
          query(collection(db, "users"), where("email", "==", user.email))
        );
        const userData = snap.empty ? null : snap.docs[0].data();
        if (userData && userData.stationboard) {
          dispatch({
            type: "SET_AUTH",
            payload: {
              currentUser: user,
              claims,
              agency: userData.agency || null,
              boardId: null,
              mode: "legacy",
              isStationboard: true,
            },
          });
          return;
        }

        // Not authorized for the Station Board.
        await signOut(auth);
        dispatch({ type: "LOGOUT" });
      } catch (err) {
        console.error("Failed to resolve auth claims:", err);
        dispatch({ type: "LOGOUT" });
      }
    });
    return () => unsub();
  }, []);

  // Subscribe to this board's config doc (machine boards only).
  useEffect(() => {
    if (state.mode !== "machine" || !state.agency || !state.boardId) return;
    const ref = doc(db, "Agencies", state.agency, "Stationboards", state.boardId);
    const unsub = onSnapshot(
      ref,
      (snap) =>
        dispatch({
          type: "SET_CONFIG",
          payload: snap.exists() ? { id: snap.id, ...snap.data() } : null,
        }),
      (err) => {
        console.error("Failed to load board config:", err);
        dispatch({ type: "SET_CONFIG", payload: null });
      }
    );
    return () => unsub();
  }, [state.mode, state.agency, state.boardId]);

  return (
    <AuthContext.Provider value={{ ...state, dispatch }}>
      {children}
    </AuthContext.Provider>
  );
};
